import { ReactNode, useMemo } from 'react';
import { css } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { callIntelKpiUtils } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';

type SentimentsMap = Record<CallIntelTypes.SentimentEnum | string, number>;

type UnhappyServiceIssueProps = {
  sentiments?: SentimentsMap | null;
  stats?: CallIntelTypes.ServiceQualityStats;
};

type InsightCounts = {
  callsAnalyzed: number;
  serviceIssueCalls: number;
  unhappyCalls: number;
  unhappyWithIssueCalls: number;
};

export type UnhappyServiceIssueInsight = {
  counts: InsightCounts;
  percent: number;
  subTitle: ReactNode;
  title: ReactNode;
};

const SubTitle = ({ count, muted }: { count: number; muted?: boolean }) => {
  const { t } = useTranslation('analytics');
  return (
    <div css={styles.subTitleWrapper}>
      <Text color='subdued' size='small'>
        {t('Unhappy + Service Issue')}
      </Text>
      <Text size='small' css={styles.secondaryText(muted)}>
        {t('{{count}} Calls', { count })}
      </Text>
    </div>
  );
};

const Title = ({ message, muted }: { message: string; muted?: boolean }) => {
  return (
    <Text css={styles.titleText(muted)} size='medium' weight='bold'>
      {message}
    </Text>
  );
};

export const useUnhappyServiceIssueInsight = ({
  sentiments,
  stats,
}: UnhappyServiceIssueProps): UnhappyServiceIssueInsight => {
  const { t } = useTranslation('analytics');

  return useMemo<UnhappyServiceIssueInsight>(() => {
    // totals
    const {
      currentStats: {
        analyzedCallsCount: callsAnalyzed = 0,
        callsWithSqCount: serviceIssueCalls = 0,
        unhappyCallsCount: unhappyWithIssueCalls = 0,
      } = {},
    } = stats ?? {};

    const unhappyCalls = Number(sentiments?.[CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE] ?? 0);

    const percent = unhappyCalls > 0 ? (unhappyWithIssueCalls / unhappyCalls) * 100 : 0;

    const counts: InsightCounts = {
      callsAnalyzed,
      serviceIssueCalls,
      unhappyCalls,
      unhappyWithIssueCalls,
    };

    let titleMessage: string;
    let muted = false;

    if (callsAnalyzed === 0) {
      // CASE 1: no calls analyzed
      titleMessage = t('No data for this period. Insights are unavailable.');
      muted = true;
    } else if (unhappyCalls === 0 && serviceIssueCalls === 0) {
      // CASE 2: no unhappy + no service issues
      titleMessage = t('Great job! No Unhappy calls or Service Issues to review!');
    } else if (unhappyCalls === 0 && serviceIssueCalls > 0) {
      // CASE 3: no unhappy, but service issues present
      titleMessage = t('No Unhappy calls! Service Issues still may need attention.');
    } else if (serviceIssueCalls === 0) {
      // CASE 4: unhappy present, but no service issues
      titleMessage = t('No Service Issues were detected in your Unhappy calls!');
    } else {
      // CASE 5: both present
      titleMessage = t('{{percent}}% of Unhappy calls have a detected Service Issue.', {
        percent: callIntelKpiUtils.formatPercentWithPrecision(percent),
      });
    }

    const subTitleNode = <SubTitle count={unhappyWithIssueCalls} muted={muted} />;
    const titleNode = <Title message={titleMessage} muted={muted} />;

    return {
      counts,
      percent,
      subTitle: subTitleNode,
      title: titleNode,
    };
  }, [sentiments, stats, t]);
};

const styles = {
  subTitleWrapper: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  titleText: (muted?: boolean) => css`
    color: ${muted ? theme.colors.neutral50 : theme.colors.neutral70};
    font-weight: ${muted ? theme.font.weight.regular : theme.font.weight.bold};
  `,
  secondaryText: (muted?: boolean) => css`
    color: ${theme.colors.neutral70};
    font-weight: ${muted ? theme.font.weight.regular : theme.font.weight.bold};
  `,
};
